/*
 * ArgoScreen.jsx — ARGO · Main screen (FIXED)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AREngine }      from '../core/AREngine';
import { AnchorManager } from '../core/AnchorManager';
import { ModelViewer }   from '../effects/ModelViewer';
import { PRODUCTS }      from '../config/products';
import {
  GlobalFonts, 
  Body, 
  Screen,
  Brand, 
  Logo,
  ScanArea, 
  Corners, 
  Corner, 
  VideoFeed, 
  Portal, 
  Beam, 
  MarkA,
  Content, 
  Heading, 
  Sub, 
  Divider, 
  CTABtn, 
  ScanIcon,
  Status, 
  LockIcon, 
  ARChip, 
  ARDot, 
  StopIcon
} from './ARLanding.styles';

export default function ArgoScreen({ onFound }) {

  const containerRef = useRef(null);
  const videoRef     = useRef(null);
  const engineRef    = useRef(null);
  const managerRef   = useRef(null);
  const viewersRef   = useRef({});
  const [ phase, setPhase ] = useState('idle');

  /* ── Start AR ── */
  const handleStart = useCallback(async () => {
    setPhase('scanning');
  }, []);

  /* ── Stop AR → quay về idle ── */
  const handleStop = useCallback(() => {
    engineRef.current?.stop();
    engineRef.current = null;
    managerRef.current = null;
    Object.values(viewersRef.current).forEach(v => v.dispose());
    viewersRef.current = {};
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setPhase('idle');
  }, []);

  /* ── Init MindAR khi phase = scanning ── */
  useEffect(() => {
    if (phase !== 'scanning') {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }
    
    const engine = new AREngine(container);
    const uniqueAnchors = new Set(Object.values(PRODUCTS).map(p => p.anchorIndex)).size;
    engine.init(uniqueAnchors);
    engineRef.current = engine;

    const manager = new AnchorManager(engine);
    managerRef.current = manager;

    Object.entries(PRODUCTS).forEach(([sku, product]) => {
      manager.register(sku, product, {

        onFound: async (sku, product, anchor) => {
          setPhase('found');
          onFound(product);
          if (!viewersRef.current[sku]) {
            const viewer = new ModelViewer(
              anchor.group, 
              product,
              engine.camera, 
              engine.hudScene, 
              engine.hudCamera
            );
            viewersRef.current[sku] = viewer;
            try { 
              await viewer.load(); 
            } catch (e) { 
              console.error(`ModelViewer [${sku}]:`, e); 
            }
          }
        },

        onLost: (sku) => {
          viewersRef.current[sku]?.pause();
        }
      })
    });

    engine.startRenderLoop(({ dt, t }) => {
      manager.update(t);
      Object.values(viewersRef.current).forEach(v => v.update(dt));
    });

    /* ── FIX: MindAR video phải visible, mirror vào Corners ── */
    engine.start().then(() => {
      const mindVideo = container.querySelector('video');
      if (mindVideo && videoRef.current) {
        const mirrorVideo = videoRef.current;
        mirrorVideo.srcObject = mindVideo.srcObject;
        mirrorVideo.muted     = true;
        mirrorVideo.playsInline = true;
        mirrorVideo.autoplay  = true;
        mirrorVideo.play().catch(() => {});
      }
    }).catch(console.error);

    return () => {
      Object.values(viewersRef.current).forEach(v => v.dispose());
      viewersRef.current = {};
      engine.stop();
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        videoRef.current.srcObject = null;
      }
    };
    /* eslint-disable-next-line */
  }, [phase === 'scanning']);

  const isScanning = phase === 'scanning' || phase === 'found';
  const isFound    = phase === 'found';

  return (
    <>
      <GlobalFonts />

      {/* 
        ── FIX: MindAR container phải visible nhưng ẩn dưới UI ──
        Không dùng visibility:hidden. Thay vào đó:
        - position: fixed, inset: 0
        - opacity: 0 (video vẫn render, MindAR vẫn đọc frame)
        - zIndex: 0 (dưới tất cả UI)
      */}
      <div
        ref={containerRef}
        style={{
          position: 'fixed', 
          inset: 0,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      <Body>
        <Screen aria-label="ARGO Camera Scan">

          <Brand>
            <Logo src="/logo.png" alt="ARGO Logo" />
          </Brand>

          <ScanArea aria-label="Khu vực quét camera">
            <Corners>
              <Corner className="tl" />
              <Corner className="tr" />
              <Corner className="bl" />
              <Corner className="br" />
            </Corners>

            <VideoFeed $visible={isScanning} $scanning={phase === 'scanning'}>
              <video 
                ref={videoRef} 
                muted 
                playsInline 
                autoPlay 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }} 
              />
            </VideoFeed>

            <Portal $hidden={isScanning}>
              <Beam />
              <MarkA src="/logo_fabi.png" alt="Mark A" />
            </Portal>

            {isScanning && (
              <div style={{
                position: 'absolute', 
                top: 12, 
                left: 0, 
                right: 0,
                display: 'flex', 
                justifyContent: 'center', 
                zIndex: 4
              }}>
                <ARChip>
                  <ARDot $found={isFound} />
                  {isFound ? 'Đã nhận diện' : 'Đang nhận diện…'}
                </ARChip>
              </div>
            )}
          </ScanArea>

          <Content>
            <Heading>
              {isScanning ? 'Đang Quét' : 'Quét Camera'}
            </Heading>
            <Sub>
              {isScanning
                ? 'Giữ camera hướng vào ấn phẩm'
                : 'Bắt đầu trải nghiệm ARGO'}
            </Sub>
            <Divider />

            <CTABtn
              type="button"
              aria-label={isScanning ? 'Dừng quét' : 'Bắt đầu quét camera'}
              onClick={isScanning ? handleStop : handleStart}
            >
              {isScanning ? <StopIcon /> : <ScanIcon />}
            </CTABtn>

            <Status>
              <LockIcon />
              <span>Bảo mật | Nhanh chóng | Trải nghiệm tức thì</span>
            </Status>
          </Content>
        </Screen>
      </Body>
    </>
  )
};
